import { useState } from 'react';
import axios from 'axios';
import { X, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { INDIAN_STATES } from '../config';
import { trackPixel } from '../lib/pixel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProductInquiryModal({ product, onClose }) {
  const { t } = useLanguage();
  const f = t.contact.form;
  const [form, setForm] = useState({
    name: '', phone: '', email: '',
    product: product?.name || '',
    state: '', city: '', message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return;
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/inquiries`, { ...form, source: 'product_inquiry' });
      trackPixel('Lead', { content_name: form.product || 'General Enquiry', content_category: 'product_inquiry' });
      setSuccess(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-4 py-3 border rounded-lg text-sm font-body outline-none transition-all duration-200 focus:ring-2 bg-white";
  const inputStyle = { borderColor: '#E5E7EB', '--tw-ring-color': '#0A3F22' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: '#E5E7EB' }}>
          <div>
            <h2 className="font-heading font-bold text-lg" style={{ color: '#0A3F22' }}>
              {product ? `Enquire: ${product.name}` : 'Product Enquiry'}
            </h2>
            <p className="text-xs mt-0.5" style={{ color: '#4A554A' }}>We'll contact you within 24 hours</p>
          </div>
          <button
            data-testid="modal-close-btn"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {success ? (
            <div className="text-center py-6">
              <CheckCircle size={48} className="mx-auto mb-3" style={{ color: '#1B6B3D' }} />
              <p className="font-heading font-semibold text-lg" style={{ color: '#0A3F22' }}>{f.success}</p>
              <button
                onClick={onClose}
                className="mt-4 px-6 py-2 rounded-lg text-white font-semibold text-sm transition-opacity hover:opacity-90"
                style={{ backgroundColor: '#0A3F22' }}
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="inquiry-form">
              <div>
                <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#4A554A' }}>{f.name} *</label>
                <input
                  data-testid="inquiry-name"
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your full name"
                  className={inputClass}
                  style={inputStyle}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#4A554A' }}>{f.phone} *</label>
                <input
                  data-testid="inquiry-phone"
                  type="tel"
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+91 XXXXX XXXXX"
                  className={inputClass}
                  style={inputStyle}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#4A554A' }}>{f.email}</label>
                <input
                  data-testid="inquiry-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="your@email.com (optional)"
                  className={inputClass}
                  style={inputStyle}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#4A554A' }}>{f.product}</label>
                <select
                  data-testid="inquiry-product"
                  value={form.product}
                  onChange={(e) => setForm({ ...form, product: e.target.value })}
                  className={inputClass}
                  style={inputStyle}
                >
                  <option value="">Select a product</option>
                  <option value="Rotavator">Rotavator</option>
                  <option value="Mini Rotavator">Mini Rotavator</option>
                  <option value="Disk Harrow">Disk Harrow</option>
                  <option value="Mulcher">Mulcher</option>
                  <option value="Fertilizer Spreader">Fertilizer Spreader</option>
                  <option value="Haloom Mulcher (Potato Mulcher)">Haloom Mulcher (Potato Mulcher)</option>
                  <option value="Spring Tine Cultivator">Spring Tine Cultivator</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#4A554A' }}>State</label>
                  <select
                    data-testid="inquiry-state"
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                    className={inputClass}
                    style={inputStyle}
                  >
                    <option value="">State</option>
                    {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#4A554A' }}>City</label>
                  <input
                    data-testid="inquiry-city"
                    type="text"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder="Your city"
                    className={inputClass}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#4A554A' }}>{f.message}</label>
                <textarea
                  data-testid="inquiry-message"
                  rows={3}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Any specific requirements..."
                  className={inputClass}
                  style={inputStyle}
                />
              </div>

              {error && <p className="text-red-600 text-xs">{error}</p>}

              <button
                data-testid="inquiry-submit"
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-lg text-white font-heading font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
                style={{ backgroundColor: '#0A3F22' }}
              >
                {loading ? 'Sending...' : f.submit}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
